import type { TemplateQuestion } from '../api/types'

/**
 * Conditional questions.
 *
 * `event_price` only makes sense once someone picks "pay later", so it carries
 *   validation_rules: { visible_when: { key: 'booking_mode', equals: 'pay_later' } }
 *
 * A question whose rule does not hold is not rendered AND not required — those
 * two must move together. Requiring something the flow never showed is what
 * made every 1-to-1 submission fail with "Missing required organizer answer".
 */

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : [value])

export function isQuestionVisible(
  question: TemplateQuestion | null | undefined,
  answers: Record<string, unknown> = {}
): boolean {
  const rule = question?.validation_rules?.visible_when
  if (!rule || !rule.key) return true

  const actual = answers[rule.key]

  if (rule.equals !== undefined) {
    return asArray(actual).some((entry) => String(entry) === String(rule.equals))
  }
  if (Array.isArray(rule.in)) {
    return asArray(actual).some((entry) => rule.in!.map(String).includes(String(entry)))
  }
  // A rule naming a key with no comparison means "answered at all".
  return actual !== undefined && actual !== null && actual !== ''
}

/** An answer counts only if it is really there. */
export function isAnswerFilled(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  // A restaurant answer is an object; it counts only once it carries a real
  // restaurants.id, so "typed something in the search box" is not an answer.
  if (value && typeof value === 'object') {
    const v = value as Record<string, unknown>
    return Boolean(v.id || v.restaurant_id)
  }
  return value !== undefined && value !== null && value !== ''
}
